import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useMfa } from '@iheartjane/menu';
import { trackError } from '@iheartjane/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useCheckout, useGoogleRecaptcha } from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';
import { useJaneUser, userQueryKeys } from '@jane/shared/data-access';

import { useCheckoutPayments } from '../../CheckoutPayments/CheckoutPaymentsProvider';
import { useHeadlessKiosk } from '../../HeadlessKioskProvider';
import {
  phoneNeedsVerification,
  shouldLaunchMfa,
  shouldSkipRecaptcha,
  trackingEventForPayment,
} from '../checkoutSubmitGates';
import { useBuildCheckoutRequest } from './useBuildCheckoutRequest';
import { useHasNewPhone } from './useHasNewPhone';
import { useMaybeUpdateUser } from './useMaybeUpdateUser';

export const useSubmitCheckout = () => {
  const maybeUpdateUser = useMaybeUpdateUser({ checkingOut: true });
  const { hasNewPhone, newPhone } = useHasNewPhone();
  const { getValue, reset, execute } = useGoogleRecaptcha();
  const { launch, requiresMfa } = useMfa();
  const { buildRequestValues } = useBuildCheckoutRequest();
  const { data: janeUser } = useJaneUser();
  const shouldSkipMfa = useFlag(FLAGS.c3SkipExtraneousMfa);

  const {
    customer: { authenticated },
    dispatches: { checkout, checkoutCancelled, checkoutError, checkoutStarted },
    store: { canpay_v2remotepay_enabled },
  } = useCheckout();
  const { isHeadlessKioskMode } = useHeadlessKiosk();
  const { selected } = useCheckoutPayments();

  const queryClient = useQueryClient();

  // Submitting checkout operates as three function. When submitted, the first function
  // checks if recaptcha validation is required. Upon successful recaptcha (or if not required),
  // the next function is called that will check if MFA is required. Upon mfa success (or if not required),
  // the actual checkout submission function will be called.
  const handleCheckout = useCallback(() => {
    maybeUpdateUser();
    const recaptchaToken = getValue();
    reset();
    const requestValues = buildRequestValues();

    checkout({
      ...requestValues,
      // Skip recaptcha token in kiosk mode
      ...(isHeadlessKioskMode ? {} : { recaptcha_token: recaptchaToken }),
    });
  }, [
    buildRequestValues,
    checkout,
    getValue,
    isHeadlessKioskMode,
    maybeUpdateUser,
    reset,
  ]);

  const handleMfa = useCallback(() => {
    const needsMfa = shouldLaunchMfa({
      authenticated,
      hasNewPhone,
      mfaAtCheckoutDisabled: shouldSkipMfa,
      needsPhoneVerification: phoneNeedsVerification(janeUser?.user),
      requiresMfa: requiresMfa(),
      selectedPaymentOption: selected,
    });

    if (!needsMfa) {
      return handleCheckout();
    }

    return launch({
      onCancel: () => checkoutCancelled(),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.janeUser() });
        handleCheckout();
      },
      newPhone: hasNewPhone ? newPhone : undefined,
    });
  }, [
    authenticated,
    checkoutCancelled,
    handleCheckout,
    hasNewPhone,
    janeUser,
    launch,
    newPhone,
    queryClient,
    requiresMfa,
    selected,
    shouldSkipMfa,
  ]);

  const handleRecaptcha = useCallback(() => {
    checkoutStarted();
    const event = trackingEventForPayment(selected);
    event && track({ event });

    const skipRecaptcha = shouldSkipRecaptcha({
      canpayV2RemotepayEnabled: canpay_v2remotepay_enabled,
      isHeadlessKioskMode,
      selectedPaymentOption: selected,
    });

    if (skipRecaptcha || getValue()) {
      return handleMfa();
    }

    try {
      return execute({
        onCancel: () => checkoutCancelled(),
        onError: () => {
          checkoutError('There was an error launching the ReCAPTCHA service');
          checkoutCancelled();
        },
        onSuccess: handleMfa,
      });
    } catch (e) {
      trackError('Google ReCAPTCHA error', { error: e });
      checkoutCancelled();
    }
  }, [
    canpay_v2remotepay_enabled,
    checkoutCancelled,
    checkoutError,
    checkoutStarted,
    execute,
    getValue,
    handleMfa,
    isHeadlessKioskMode,
    selected,
  ]);

  return {
    submit: handleRecaptcha,
  };
};
